import * as React from 'react';
import {
  Box,
  FlexBox,
  Logo,
  Button,
  Typography,
  Backdrop,
  ButtonProps,
  SvgIconButton,
  Grid,
  MuiBox,
  SvgIcon
} from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import { useSelector } from 'react-redux';
import { selectors as kioskSelectors, actions as kioskActions, KioskHardware } from 'services/kiosk';
import { DataEntryOverlay } from 'components/DataEntryOverlay';
import { TicketStockOverlayProps } from '../types';
import { actions as eventActions, EventPayload, eventNames } from 'services/events';
import get from 'lodash.get';
import { useStyles } from '../styled';

/**
 * Overlay used for choosing how many tickets to load
 */
export const TicketStockOverlay = (props: TicketStockOverlayProps) => {
  const { onCancel, onConfirm, message, kioskHardwareId } = props;
  const classes = useStyles();

  // Actions and Selectors
  const kioskPrinters = useSelector(kioskSelectors.selectPrinters);
  const loading = useSelector(kioskSelectors.selectLoading);

  const { updateKioskHardware, logEvent } = useActions({
    updateKioskHardware: kioskActions.updateKioskHardware,
    logEvent: eventActions.logEvent
  });

  const kioskPrinter = kioskPrinters.find((p) => p.id === kioskHardwareId) as KioskHardware;
  const restockAmounts: (string | number)[] = get(kioskPrinter, 'configuration.restockAmounts', []);

  // Show custom ticket stock overlay
  const [showCustomTicketStock, setShowCustomTicketStock] = React.useState(false);

  // ***************
  // ** FUNCTIONS **
  // ***************

  // logs event
  const logTicketEvent = () => {
    const eventPayload: EventPayload = {
      event: eventNames.KIOSK_TICKET_STOCK_RELOADED,
      category: 'ticket_stock_reload',
      includeActor: true
    };
    logEvent(eventPayload);
  };

  // on stock click
  const onStockClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    /*
     * if the button clicked is custom - open the custom ticket stocket overlay
     * else - call updateKioskHardware, logTicketEvent, and props.onConfirm
     */
    if (e.currentTarget.value.includes('Custom Amount')) {
      setShowCustomTicketStock(true);
    } else {
      updateKioskHardware({
        id: kioskHardwareId,
        values: { currentStock: e.currentTarget.innerText }
      });
      logTicketEvent();
      if (onConfirm) onConfirm();
    }
  };

  // on custom ticket stock confirm - call updateKioskHardware, logTicketEvent, and props.onConfirm, close overlay
  const onCustomConfirm = (value: string) => {
    updateKioskHardware({
      id: kioskHardwareId,
      values: { currentStock: value }
    });
    logTicketEvent();
    setShowCustomTicketStock(false);
    if (onConfirm) onConfirm();
  };

  const buttonProps: ButtonProps = {
    appearance: 'primary',
    customSize: 'giant',
    width: 435,
    onClick: onStockClick,
    isLoading: loading
  };

  return (
    <>
      <Backdrop open>
        <Box bg="white" padding="5 0" height="100vh" width="100%" sx={{ borderRadius: '32px 32px 0 0' }}>
          <MuiBox className={classes.ticketStockBox}>
            {/* Header */}
            <FlexBox justify="between">
              <Logo />
              <SvgIconButton data-testid="ticket-stock-x" onClick={onCancel} appearance="transparent">
                <SvgIcon name="exit_circle" size={65} />
              </SvgIconButton>
            </FlexBox>

            {/* Content */}
            <Grid item md={12} xl={12}>
              <Box margin="0 0 5" padding="10 0 0">
                <Typography fontSize={64} fontWeight={600} align="center">
                  Load Stock
                </Typography>
              </Box>
              <Box>
                <Typography fontSize={30} fontWeight={400} align="center">
                  {message || 'How much stock did you load?'}
                </Typography>
              </Box>
            </Grid>

            <Grid container spacing={10} sx={{ padding: '75px 0 0 0' }} justifyContent="center" alignItems="center">
              {restockAmounts.map((amount, idx) => (
                <Grid item md={12} xl={3} lg={3} key={`restock-amount-${idx}`}>
                  <FlexBox sx={{ justifyContent: 'center' }}>
                    <Button
                      value={amount.toString()}
                      {...buttonProps}
                      data-testid="stock-button"
                      className={classes.ticketStockButtons}
                    >
                      {amount}
                    </Button>
                  </FlexBox>
                </Grid>
              ))}
            </Grid>
          </MuiBox>
        </Box>
      </Backdrop>

      {/* Custom Ticket Stock Overlay */}
      {showCustomTicketStock && (
        <DataEntryOverlay
          title={'Load Stock'}
          description={'How much stock did you load?'}
          inputHint={'Enter Stock Count'}
          inputType="numpad"
          nextText="Enter"
          onCancel={() => setShowCustomTicketStock(false)}
          onConfirm={onCustomConfirm}
          loading={loading}
          hideForwardPrompt
        />
      )}
    </>
  );
};
